import type { AvailableField } from "@/utils/ruleDefinitions";
import type { RuleCondition } from "@/components/RuleConditionRow";

/**
 * Plain-language helpers for the Offer Designer.
 *
 * The rule builder is authored on every screen size, so the same condition has
 * to read as a sentence in three places: the live preview, each condition's
 * inline summary, and the saved-rule list. Centralizing the phrasing here keeps
 * those in sync and makes the logic unit-testable without a DOM.
 */

/** Combinator spelled out for humans. `AND` -> ALL, `OR` -> ANY. */
export const combinatorWord = (combinator: string | null | undefined): "ALL" | "ANY" =>
  combinator === "OR" ? "ANY" : "ALL";

/** "Match ALL of" / "Match ANY of" heading for a group of conditions. */
export const combinatorHeading = (combinator: string | null | undefined): string =>
  `Match ${combinatorWord(combinator)} of`;

/** The little connector shown between stacked conditions/groups. */
export const combinatorJoiner = (combinator: string | null | undefined): "AND" | "OR" =>
  combinator === "OR" ? "OR" : "AND";

// Operators phrased as verbs. Anything not listed falls back to a spaced-out
// version of the raw operator key, so a new operator still renders sanely.
const OPERATOR_PHRASES: Record<string, string> = {
  equals: "is",
  not_equals: "is not",
  contains: "contains",
  starts_with: "starts with",
  ends_with: "ends with",
  greater_than: "is greater than",
  less_than: "is less than",
  greater_than_or_equal_to: "is at least",
  less_than_or_equal_to: "is at most",
  is_on: "is on",
  is_before: "is before",
  is_after: "is after",
  contains_any: "contains any of",
  contains_all: "contains all of",
  has_key: "has key",
  has_any_key: "has any key",
  has_all_keys: "has all keys",
  contains_item_id: "contains item",
  contains_any_item_ids: "contains any item in",
};

// Operators that stand on their own with no value to the right of them.
const VALUELESS_OPERATORS: Record<string, string> = {
  is_true: "is true",
  is_false: "is false",
  is_empty: "is empty",
  is_not_empty: "is not empty",
};

const PLACEHOLDER = "…";

const displayValue = (value: RuleCondition["value"]): string => {
  if (Array.isArray(value)) {
    const parts = value.filter((v) => v !== null && v !== undefined && v !== "");
    return parts.length ? parts.join(", ") : PLACEHOLDER;
  }
  if (value === null || value === undefined || value === "") return PLACEHOLDER;
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value);
};

/**
 * Resolve a stored field key to its definition. Rules are authored with dotted
 * keys (`order.subtotal`) but persisted through the update RPC with underscores
 * (`order_subtotal`), so both spellings have to resolve to the same field.
 */
export const findField = (
  field: string,
  fields: readonly AvailableField[]
): AvailableField | undefined =>
  fields.find((f) => f.value === field || f.value.replace(/\./g, "_") === field);

/** The canonical dotted key for a stored field, or the raw key if unknown. */
export const canonicalFieldKey = (field: string, fields: readonly AvailableField[]): string =>
  findField(field, fields)?.value ?? field;

/**
 * Turn one condition into a readable clause, e.g. "Subtotal is greater than 25"
 * or "Is Loyalty Member is true". `fields` supplies the human label for the
 * field key; an unknown field falls back to its raw key.
 */
export const formatCondition = (
  condition: RuleCondition,
  fields: readonly AvailableField[]
): string => {
  const fieldLabel = findField(condition.field, fields)?.label ?? condition.field;
  const { operator } = condition;

  if (operator in VALUELESS_OPERATORS) {
    return `${fieldLabel} ${VALUELESS_OPERATORS[operator]}`;
  }

  if (operator === "is_between" && Array.isArray(condition.value)) {
    const [min, max] = condition.value;
    return `${fieldLabel} is between ${min ?? PLACEHOLDER} and ${max ?? PLACEHOLDER}`;
  }

  const verb = OPERATOR_PHRASES[operator] ?? operator.replace(/_/g, " ");
  return `${fieldLabel} ${verb} ${displayValue(condition.value)}`.trim();
};
